import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authService, RegisterPayload, VerifyOtpPayload } from '@/services/auth.service';
import { useAppDispatch, useAppSelector } from '@/store';
import { clearAuth, setAuth } from '@/store/authSlice';
import { QUERY_KEYS } from '@/utils/constants';

const useAuth = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const sendOtpMutation = useMutation({
    mutationFn: (mobile: string) => authService.sendOtp(mobile),
  });

  const verifyOtpMutation = useMutation({
    mutationFn: (payload: VerifyOtpPayload) => authService.verifyOtp(payload),
    onSuccess: ({ accessToken, user: loggedInUser }) => {
      dispatch(setAuth({ accessToken, user: loggedInUser }));
    },
  });

  const registerSendOtpMutation = useMutation({
    mutationFn: (mobile: string) => authService.registerSendOtp(mobile),
  });

  // Registration does NOT log the user in - the account is pending admin
  // approval, so we never call setAuth here.
  const registerMutation = useMutation({
    mutationFn: (payload: RegisterPayload) => authService.register(payload),
  });

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      dispatch(clearAuth());
      queryClient.clear();
    }
  };

  const meQuery = useQuery({
    queryKey: QUERY_KEYS.AUTH.ME,
    queryFn: authService.getMe,
    enabled: isAuthenticated,
  });

  return {
    user,
    isAuthenticated,
    sendOtp: sendOtpMutation.mutateAsync,
    isSendingOtp: sendOtpMutation.isPending,
    sendOtpError: sendOtpMutation.error,
    verifyOtp: verifyOtpMutation.mutateAsync,
    isVerifyingOtp: verifyOtpMutation.isPending,
    verifyOtpError: verifyOtpMutation.error,
    registerSendOtp: registerSendOtpMutation.mutateAsync,
    isSendingRegisterOtp: registerSendOtpMutation.isPending,
    register: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    logout,
    meQuery,
  };
};

export default useAuth;
